import { create } from "zustand";
import { preferences } from "@/app/platform";

/**
 * KILOS OR POUNDS — one choice, made in first run, applied to every weight the app prints.
 *
 * 🚨 THE DATABASE, THE CONTRACT AND THE ENGINE ARE METRIC AND STAY METRIC. `athlete_body
 * .body_mass_kg`, `athlete_limits.one_rep_max_kg` and every `kilos` on the wire are kilograms,
 * and none of them gains a unit column. A stored number that could be either unit is a number
 * nobody can compare to last month's, and the engine prices a load off a one-rep max without
 * ever asking whose phone it came from.
 *
 * So this is a DISPLAY layer and an INPUT layer, and nothing else: `showWeight` converts on
 * the way out and `weightToKilos` converts on the way back in. Everything between them is
 * kilos. That is also why the choice is a device PREFERENCE rather than a profile field —
 * it says nothing about the athlete, only about the phone they are reading.
 *
 * ⚠️ DISTANCES ARE NOT CONVERTED, deliberately and for now. A metcon line's unit comes off the
 * engine as its own word (`metres`, `calories`, `reps`, `seconds`), a 400 m run is a
 * prescription rather than a measurement, and rewriting it as 437 yards would be inventing a
 * workout nobody wrote. Height is not converted either, because height is not collected — the
 * body step asks for mass, a level and a sex, and there is no height column to feed.
 *
 * ⚠️ IT IS `UserDefaults`, NOT `localStorage` — the same argument `app/i18n/index.ts` makes
 * about the language, for the same reason: WebKit may evict its own store, and somebody who
 * chose pounds finding kilos one morning has nothing to report.
 */
export const UNIT_SYSTEMS = ["metric", "imperial"] as const;

export type UnitSystem = (typeof UNIT_SYSTEMS)[number];

/** The device's answer, under the same `app.` prefix the language and the query cache use. */
export const UNIT_SYSTEM_KEY = "app.units";

/** The international pound, exactly. Defined, not measured — so it is written out in full. */
const KILOS_PER_POUND = 0.45359237;

/** What a weight is called in each system. Not translated: `kg` and `lb` are the same word. */
export const WEIGHT_UNIT: Readonly<Record<UnitSystem, string>> = {
  metric: "kg",
  imperial: "lb",
};

/**
 * THE SMALLEST STEP THE WEIGHT STEPPER MOVES BY, IN EACH SYSTEM — and they are not conversions
 * of one another on purpose.
 *
 * ±2.5 kg is the smallest pair of plates a metric gym owns; ±5 lb is the smallest pair an
 * imperial one does. Converting 2.5 kg would give 5.5 lb, which is a number no rack contains,
 * and a stepper that lands between plates is worse than one that is 0.2 kg out.
 */
export const WEIGHT_STEP: Readonly<Record<UnitSystem, number>> = {
  metric: 2.5,
  imperial: 5,
};

/** Kilos → the athlete's unit, unrounded. Rounding is a rendering decision — see `showWeight`. */
export function weightFromKilos(kilos: number, system: UnitSystem): number {
  return system === "imperial" ? kilos / KILOS_PER_POUND : kilos;
}

/** The athlete's unit → kilos, which is the only shape that may reach the service. */
export function weightToKilos(amount: number, system: UnitSystem): number {
  return system === "imperial" ? amount * KILOS_PER_POUND : amount;
}

/**
 * A WEIGHT AS A SCREEN PRINTS IT — the number only, without its unit.
 *
 * ONE DECIMAL IN KILOS, WHOLE POUNDS. 137.5 kg is a real load and 137.50 is not how it is
 * said; a pound is small enough that half of one is noise, and 302.9 lb reads as a scale
 * reading rather than as what is on the bar.
 */
export function showWeight(kilos: number, system: UnitSystem): string {
  const amount = weightFromKilos(kilos, system);
  return system === "imperial"
    ? String(Math.round(amount))
    : String(Math.round(amount * 10) / 10);
}

interface UnitState {
  system: UnitSystem;
  /** True once the stored answer has been read back — before that the default is a guess. */
  hydrated: boolean;
  choose: (system: UnitSystem) => void;
}

/**
 * INTERFACE STATE, WHICH IS THE ONLY KIND A STORE MAY HOLD. Nothing here came from the server
 * and nothing here may: a unit is how this phone reads a number, not a fact about an athlete.
 *
 * METRIC IS THE DEFAULT because the whole stack is metric and because a wrong default costs
 * one tap on one screen. It is deliberately NOT guessed from the device locale: a German
 * living in Texas trains in pounds, and a silent guess that is right most of the time is the
 * kind that nobody notices is wrong until they load the bar.
 */
export const useUnitStore = create<UnitState>((set) => ({
  system: "metric",
  hydrated: false,
  choose: (system) => {
    set({ system, hydrated: true });
    // Discarded rather than awaited: the screen has already changed, and a preference store
    // that is full is not a reason to refuse the choice.
    void preferences.set(UNIT_SYSTEM_KEY, system).catch(() => {});
  },
}));

/**
 * READ WHAT WAS CHOSEN LAST TIME. Called once at start-up, beside everything else that
 * happens once — a component that did this in an effect would re-read it on every mount and
 * would race its own writes.
 */
export async function hydrateUnitSystem(): Promise<void> {
  const stored = await preferences.get(UNIT_SYSTEM_KEY).catch(() => null);
  const system = UNIT_SYSTEMS.find((one) => one === stored);
  useUnitStore.setState({ system: system ?? "metric", hydrated: true });
}

/** The chosen system, and the two things every screen that prints a weight needs from it. */
export function useUnits(): {
  system: UnitSystem;
  unit: string;
  step: number;
  show: (kilos: number) => string;
  toKilos: (amount: number) => number;
  fromKilos: (kilos: number) => number;
} {
  const system = useUnitStore((state) => state.system);
  return {
    system,
    unit: WEIGHT_UNIT[system],
    step: WEIGHT_STEP[system],
    show: (kilos) => showWeight(kilos, system),
    toKilos: (amount) => weightToKilos(amount, system),
    fromKilos: (kilos) => weightFromKilos(kilos, system),
  };
}
